'use client';

import React from 'react';
import Link from 'next/link';
import { CALCULATOR_ITEMS, CalculatorItem, renderCalculatorIcon } from '@/lib/config/calculators';
import { Grid, Sparkles, ArrowRight } from 'lucide-react';

const CATEGORIES: CalculatorItem['category'][] = [
  'Vedic Core',
  'Compatibility',
  'Doshas & Transits',
  'Advanced Systems',
  'Numerology & Fun'
];

export default function AllCalculatorsPage() {
  return (
    <div className="space-y-12 max-w-6xl mx-auto px-4 py-8 font-['Outfit']">
      {/* Page Header Banner */}
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-[rgba(5,10,36,0.7)] backdrop-blur-xl p-6 sm:p-10 shadow-xl relative overflow-hidden space-y-4">
        <div className="flex items-center gap-2 text-[#5ab4ff] text-[10px] font-bold uppercase tracking-[0.12em] font-mono">
          <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
          <span>Vedic &amp; Astronomical Platform Tools</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-['Lexend'] font-medium text-white flex items-center gap-3">
          <Grid className="w-7 h-7 text-[#5ab4ff]" />
          <span>All Astrology &amp; Numerology Calculators</span>
        </h1>

        <p className="text-xs sm:text-sm text-white/60 max-w-3xl leading-relaxed">
          Browse our complete suite of {CALCULATOR_ITEMS.length} classical Vedic astrology, planetary transit, Ashtakavarga, Shadbala, compatibility, and numerology calculation tools.
        </p>
      </div>

      {/* Categorized Card Grid */}
      <div className="space-y-10">
        {CATEGORIES.map((category) => {
          const categoryItems = CALCULATOR_ITEMS.filter((item) => item.category === category);
          if (categoryItems.length === 0) return null;

          return (
            <div key={category} className="space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-2">
                <h2 className="text-xl font-['Lexend'] font-medium text-white flex items-center gap-2">
                  <span>{category}</span>
                  <span className="text-xs font-mono font-normal text-white/50">({categoryItems.length} tools)</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryItems.map((item) => (
                  <Link
                    key={item.id}
                    href={item.href}
                    className="group rounded-2xl border border-white/10 bg-[rgba(5,10,36,0.6)] backdrop-blur-md p-6 shadow-md flex flex-col justify-between hover:border-[#5ab4ff]/50 hover:bg-white/[0.04] transition-all duration-200"
                    data-testid={`calculator-card-${item.id}`}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/10">
                          {renderCalculatorIcon(item.iconName, "w-5 h-5 text-[#5ab4ff]")}
                        </div>
                        {item.isTopPriority && (
                          <span className="text-[10px] uppercase font-mono font-semibold px-2.5 py-0.5 rounded-full bg-[#5ab4ff]/15 text-[#5ab4ff] border border-[#5ab4ff]/30">
                            Popular
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-['Lexend'] font-semibold text-white group-hover:text-[#5ab4ff] transition-colors">
                        {item.label}
                      </h3>

                      <p className="text-xs text-white/60 leading-relaxed">
                        {item.description}
                      </p>
                    </div>

                    <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between text-xs font-semibold text-[#5ab4ff] group-hover:text-white transition-colors">
                      <span>Calculate Now</span>
                      <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
